import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SelectClienteFinal } from "@/components/ui/SelectClienteFinal";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import type { Aprendiz } from "@/interfaces/user.interface";
import { useUpdateAprendizWithUsuario } from "@/hooks/aprendices/useUpdateAprendizWithUsuario";

const schema = z.object({
  usuario: z.object({
    nombreUsuario: z.string().min(2, "Nombre de usuario requerido"),
    nombre: z.string().min(1),
    apellidoPaterno: z.string().min(1),
    apellidoMaterno: z.string().optional(),
    correo: z.string().email(),
  }),
  aprendiz: z.object({
    idClienteFinal: z.number().min(1, "Id del cliente final requerido"),
    ultimaActividad: z.date().optional(),
  }),
});

type FormData = z.infer<typeof schema>;

export function EditAprendizForm({
  data,
  queryKey = ["aprendices"],
  close,
}: {
  data: Aprendiz;
  queryKey?: ReadonlyArray<unknown>;
  close: () => void;
}) {
  const aprendiz = data;
  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      usuario: {
        nombreUsuario: aprendiz.usuario.nombreUsuario,
        nombre: aprendiz.usuario.nombre,
        apellidoPaterno: aprendiz.usuario.apellidoPaterno,
        apellidoMaterno: aprendiz.usuario.apellidoMaterno ?? "",
        correo: aprendiz.usuario.correo,
      },
      aprendiz: {
        idClienteFinal: aprendiz.idClienteFinal,
        ultimaActividad: aprendiz.ultimaActividad
          ? new Date(aprendiz.ultimaActividad)
          : undefined,
      },
    },
  });

  const { mutate, isPending } = useUpdateAprendizWithUsuario(queryKey);

  const onSubmit = (data: FormData) => {
    mutate(
      {
        id: typeof aprendiz.id === "number" ? aprendiz.id : Number(aprendiz.id),
        data,
      },
      {
        onSuccess: () => {
          close();
        },
      },
    );
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4 sm:space-y-6"
      >
        <FormField
          control={form.control}
          name="aprendiz.idClienteFinal"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Cliente final</FormLabel>
              <FormControl>
                <SelectClienteFinal
                  value={
                    field.value && field.value !== 0
                      ? { value: field.value, label: "" }
                      : null
                  }
                  onChange={(option) =>
                    field.onChange(option ? option.value : 0)
                  }
                  placeholder="Selecciona un cliente final"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="usuario.nombreUsuario"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Nombre de usuario</FormLabel>
              <FormControl>
                <Input placeholder="Nombre de usuario" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="usuario.nombre"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Nombre</FormLabel>
              <FormControl>
                <Input placeholder="Nombre" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="usuario.apellidoPaterno"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Apellido paterno</FormLabel>
              <FormControl>
                <Input placeholder="Apellido paterno" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="usuario.apellidoMaterno"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Apellido materno</FormLabel>
              <FormControl>
                <Input placeholder="Apellido materno" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="usuario.correo"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Correo</FormLabel>
              <FormControl>
                <Input placeholder="correo@ejemplo.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          disabled={isPending}
          className="w-full h-10 sm:h-11 text-sm sm:text-base"
        >
          {isPending ? "Guardando..." : "Guardar cambios"}
        </Button>
      </form>
    </Form>
  );
}
